/**
 * PATH: packages/quote-builder/src/grid/SheetGridFormulaBar.tsx
 * FILE_ID: QB-GRID-FORMULABAR-001
 * PURPOSE: The formula bar that pairs with SheetGrid — shows the focused
 *   cell's label + raw value, edits write back through the caller, and
 *   (new) surfaces the engine's function catalogue: autocomplete while
 *   typing "=su…", a signature hint inside a call, an fx browse button,
 *   and a red error slot for the focused cell.
 * WHY: ARC-3 (U06) extraction + operator 2026-07-06: "=SUM … is not
 *   there" — the catalogue existed in the engine but was invisible.
 * FLOW: (label, value) → input → onChangeValue(raw)
 *       value+caret → suggestFormulaFunctions/getActiveFunctionContext
 *       → menu insert ("SUM(") → onChangeValue
 * DEPENDENCIES: ./formulaCatalog (suggestions + signature context)
 */
import { useEffect, useMemo, useRef, useState } from 'react'

import {
  extractTrailingIdentifier,
  getActiveFunctionContext,
  suggestFormulaFunctions,
  type FormulaFunctionDoc,
} from './formulaCatalog'

export interface SheetGridFormulaBarProps {
  /** Focused-cell label, e.g. "A1" or "R3 · material". Empty = no focus. */
  cellLabel: string
  /** Raw value of the focused cell. */
  value: string
  onChangeValue: (next: string) => void
  placeholder?: string
  /** Right-hand hint, e.g. "Ctrl/⌘+D fills down · paste multi-cell from Excel". */
  hint?: string
  /**
   * Error explanation for the focused cell (e.g. "Missing closing
   * bracket"). Replaces the hint with red text while present.
   */
  error?: string
  /** Show the fx autocomplete + browse menu (default true). */
  enableFunctionMenu?: boolean
  disabled?: boolean
}

const MENU_LIMIT = 12

function isComposing(value: string): boolean {
  return value.trimStart().startsWith('=')
}

export function SheetGridFormulaBar({
  cellLabel,
  value,
  onChangeValue,
  placeholder,
  hint,
  error,
  enableFunctionMenu = true,
  disabled,
}: SheetGridFormulaBarProps) {
  const hasFocus = cellLabel !== ''
  const inputRef = useRef<HTMLInputElement | null>(null)
  const [menuDismissed, setMenuDismissed] = useState(false)
  const [browseOpen, setBrowseOpen] = useState(false)
  const [highlight, setHighlight] = useState(-1)

  // The identifier fragment being typed (menu trigger) — never inside "…".
  const fragment = useMemo(
    () => (isComposing(value) ? extractTrailingIdentifier(value) : ''),
    [value],
  )

  const suggestions = useMemo<FormulaFunctionDoc[]>(() => {
    if (!enableFunctionMenu) return []
    if (browseOpen) return suggestFormulaFunctions('')
    if (!isComposing(value) || menuDismissed || fragment.length === 0) return []
    return suggestFormulaFunctions(fragment)
  }, [enableFunctionMenu, browseOpen, value, menuDismissed, fragment])

  const menuOpen = suggestions.length > 0

  // Signature hint for the innermost open call, e.g. "IF(condition, then, [else])".
  const signature = useMemo(() => {
    if (!isComposing(value) || menuOpen) return null
    return getActiveFunctionContext(value)?.signature ?? null
  }, [value, menuOpen])

  useEffect(() => {
    setHighlight(-1)
  }, [fragment, browseOpen])
  useEffect(() => {
    // Fresh typing re-arms the menu after an Escape dismissal.
    setMenuDismissed(false)
    setBrowseOpen(false)
  }, [value])

  const insertFunction = (doc: FormulaFunctionDoc) => {
    const next = browseOpen || fragment === ''
      ? `${value}${doc.name}(`
      : value.slice(0, value.length - fragment.length) + `${doc.name}(`
    onChangeValue(next)
    setBrowseOpen(false)
    setHighlight(-1)
    inputRef.current?.focus()
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!menuOpen) return
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault()
      const max = Math.min(suggestions.length, MENU_LIMIT) - 1
      setHighlight((h) => (e.key === 'ArrowDown' ? Math.min(max, h + 1) : Math.max(-1, h - 1)))
      return
    }
    if ((e.key === 'Enter' || e.key === 'Tab') && highlight >= 0 && highlight < suggestions.length) {
      e.preventDefault()
      insertFunction(suggestions[highlight])
      return
    }
    if (e.key === 'Escape') {
      e.preventDefault()
      setMenuDismissed(true)
      setBrowseOpen(false)
      setHighlight(-1)
    }
  }

  return (
    <div className="relative">
      <div className="flex items-center gap-2 rounded-md border border-border bg-muted/30 px-2 py-1.5 text-[11px]">
        <span className="font-mono text-muted-foreground">
          {hasFocus ? cellLabel : 'No cell selected'}
        </span>
        {enableFunctionMenu ? (
          <button
            type="button"
            aria-label="Browse functions"
            title="Browse functions (SUM, IF, ROUND…)"
            disabled={disabled || !hasFocus}
            onClick={() => setBrowseOpen((o) => !o)}
            className="rounded border border-border px-1 py-0.5 font-mono text-[10px] italic text-muted-foreground hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
            data-testid="sheet-grid-fx-button"
          >
            fx
          </button>
        ) : null}
        <div className="mx-1 h-3 w-px bg-border" />
        <input
          ref={inputRef}
          type="text"
          aria-label={hasFocus ? `Formula for ${cellLabel}` : 'Formula bar'}
          data-testid="sheet-grid-formula-bar"
          data-sheet-grid-formula-input=""
          value={value}
          onChange={(e) => onChangeValue(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={disabled || !hasFocus}
          placeholder={placeholder ?? (hasFocus ? 'Enter value…' : 'Click a cell to edit')}
          // MR4 — 16px below sm: iOS Safari auto-zooms the page when a
          // focused input's font-size is <16px, which wrecked the modal
          // layout on phones. Desktop keeps the compact 11px.
          className="flex-1 rounded border border-border bg-background px-2 py-1 font-mono text-[16px] focus:outline-none focus:ring-1 focus:ring-primary disabled:cursor-not-allowed disabled:opacity-60 sm:py-0.5 sm:text-[11px]"
        />
        {error ? (
          <span
            className="text-[10px] font-medium text-red-600 dark:text-red-400"
            data-testid="sheet-grid-formula-bar-error"
            role="alert"
          >
            {error}
          </span>
        ) : signature ? (
          <span
            className="font-mono text-[10px] text-muted-foreground"
            data-testid="sheet-grid-formula-bar-signature"
          >
            {signature}
          </span>
        ) : hint ? (
          <span className="text-[10px] text-muted-foreground">{hint}</span>
        ) : null}
      </div>

      {menuOpen ? (
        <div
          className="absolute left-16 top-full z-50 mt-px max-h-56 w-[320px] overflow-y-auto rounded-b-md border border-border bg-background text-left shadow-xl"
          data-testid="sheet-grid-fx-menu"
        >
          {suggestions.slice(0, MENU_LIMIT).map((s, i) => (
            <button
              key={s.name}
              type="button"
              // mousedown (not click) so the input never blurs.
              onMouseDown={(e) => {
                e.preventDefault()
                insertFunction(s)
              }}
              className={[
                'flex w-full items-center justify-between gap-2 px-2 py-1 text-left hover:bg-muted',
                i === highlight ? 'bg-muted' : '',
              ].join(' ')}
            >
              <span className="min-w-0">
                <span className="block truncate font-mono text-[11px] font-semibold">{s.signature}</span>
                <span className="block truncate text-[10px] text-muted-foreground">{s.description}</span>
              </span>
            </button>
          ))}
        </div>
      ) : null}
    </div>
  )
}
